import { useCallback, useContext, useEffect, useState } from "react"
import { Navigate, useNavigate, useParams } from "react-router-dom"
import { digitalManagerApi } from "../../../../../services/api"
import type { DiMaDetailsHeaderProps } from "../../../../components/DiMaDetailsHeader"
import { DiMaProgress } from "../../../../components/DiMaProgress"
import { DetailsHeaderTab } from "../../../../components/Helpers"
import { UserContext } from "../../../../context/UserContext"
import { ApplicationPermissionUserGet } from "../../../../models/ApplicationPermissionUser"
import { UserApplicationPermissionDetails } from "./details_tab/UserApplicationPermissionDetails"
import { useHashTab } from "../../../../../hooks/useHashTab"
import { UserApplicationPermissionProperties } from "./properties_tab/UserApplicationPermissionProperties"

export function UserApplicationPermissionContainer() {
  const { userId, applicationId, permissionId } = useParams()
  const { user, isLoadingUser } = useContext(UserContext)

  const { activeTab, handleTabChange } = useHashTab("details")
  const [userApplicationPermission, setUserApplicationPermission] =
    useState<ApplicationPermissionUserGet>()
  const [reloadEntity, setReloadEntity] = useState<boolean>(true)
  const navigate = useNavigate()
  const returnURL = `/users/${userId}#apps-permissions`

  useEffect(() => {
    if (userId && applicationId && permissionId && reloadEntity === true) {
      digitalManagerApi
        .get<ApplicationPermissionUserGet>(
          `/users/${userId}/applications/${applicationId}/permissions/${permissionId}`
        )
        .then((res) => {
          setUserApplicationPermission(res.data)
          setReloadEntity(false)
        })
        .catch(() => {
          navigate(returnURL)
        })
    }
  }, [applicationId, navigate, permissionId, reloadEntity, returnURL, userId])

  const reloadUserApplicationPermission = useCallback(() => setReloadEntity(true), [])

  if (!userId || !applicationId || !permissionId) {
    return <Navigate to={returnURL} />
  }

  if (!userApplicationPermission || !user || isLoadingUser) {
    return <DiMaProgress />
  }

  const detailsHeaderProps: DiMaDetailsHeaderProps = {
    url: returnURL,
    title: userApplicationPermission.permission.name,
    label: "Permission ID",
    subtitle: userApplicationPermission.permission.id,
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", flexGrow: 1 }}>
      <DetailsHeaderTab
        detailsHeaderProps={detailsHeaderProps}
        tabItems={[
          { id: "details", label: "Details" },
          { id: "properties", label: "Properties" },
        ]}
        activeTab={activeTab}
        onTabChange={handleTabChange}
      />

      {activeTab === "details" && (
        <UserApplicationPermissionDetails
          userApplicationPermission={userApplicationPermission}
          setUserApplicationPermission={setUserApplicationPermission}
        />
      )}

      {activeTab === "properties" && (
        <UserApplicationPermissionProperties
          userApplicationPermission={userApplicationPermission}
          reloadUserApplicationPermission={reloadUserApplicationPermission}
          user={user}
        />
      )}
    </div>
  )
}
